// Validation utility functions following Task 21 specification
// Individual validator functions that return null (valid) or error message (invalid)

import { ValidationRule, ValidatorFactory, ValidationContext } from '../types'

/**
 * Individual validation functions following Task 21.1 specification
 * Each function returns null if valid, or error message if invalid
 * Supports internationalization with optional i18n parameter
 */

export const required = (message = 'This field is required') => ({
  name: 'required',
  message,
  validate: (value: any, i18n?: (key: string) => string): string | null => {
    const errorMessage = i18n ? i18n('validation.required') : message
    
    if (Array.isArray(value)) {
      return value.length > 0 ? null : errorMessage
    }
    if (typeof value === 'string') {
      return value.trim().length > 0 ? null : errorMessage
    }
    return (value != null && value !== undefined) ? null : errorMessage
  }
})

export const email = (message = 'Please enter a valid email address') => ({
  name: 'email',
  message,
  validate: (value: string, i18n?: (key: string) => string): string | null => {
    if (!value) return null // Let required rule handle empty values
    
    const errorMessage = i18n ? i18n('validation.email') : message
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value) ? null : errorMessage
  }
})

export const minLength = (length: number, message?: string) => ({
  name: 'minLength',
  message: message || `Must be at least ${length} characters`,
  validate: (value: string, i18n?: (key: string) => string): string | null => {
    if (!value) return null
    
    const errorMessage = i18n 
      ? i18n('validation.minLength').replace('{length}', length.toString())
      : (message || `Must be at least ${length} characters`)
      
    return value.length >= length ? null : errorMessage
  }
})

export const maxLength = (length: number, message?: string) => ({
  name: 'maxLength',
  message: message || `Must be no more than ${length} characters`,
  validate: (value: string, i18n?: (key: string) => string): string | null => {
    if (!value) return null
    
    const errorMessage = i18n 
      ? i18n('validation.maxLength').replace('{length}', length.toString())
      : (message || `Must be no more than ${length} characters`)
      
    return value.length <= length ? null : errorMessage
  }
})

export const pattern = (regex: RegExp, message?: string) => ({
  name: 'pattern',
  message: message || 'Value does not match required pattern',
  validate: (value: string, i18n?: (key: string) => string): string | null => {
    if (!value) return null
    
    const errorMessage = i18n ? i18n('validation.pattern') : (message || 'Value does not match required pattern')
    return regex.test(value) ? null : errorMessage
  }
})

export const numeric = (message = 'Please enter a valid number') => ({
  name: 'numeric',
  message,
  validate: (value: string, i18n?: (key: string) => string): string | null => {
    if (!value) return null
    
    const errorMessage = i18n ? i18n('validation.numeric') : message
    return !isNaN(Number(value)) ? null : errorMessage
  }
})

export const phone = (message = 'Please enter a valid phone number') => ({
  name: 'phone', 
  message,
  validate: (value: string, i18n?: (key: string) => string): string | null => {
    if (!value) return null
    
    const errorMessage = i18n ? i18n('validation.phone') : message
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
    return phoneRegex.test(value.replace(/[\s\-\(\)]/g, '')) ? null : errorMessage
  }
})

export const url = (message = 'Please enter a valid URL') => ({
  name: 'url',
  message,
  validate: (value: string, i18n?: (key: string) => string): string | null => {
    if (!value) return null
    
    const errorMessage = i18n ? i18n('validation.url') : message
    try {
      new URL(value)
      return null
    } catch {
      return errorMessage
    }
  }
})

export const custom = (name: string, validator: (value: any, i18n?: (key: string) => string) => string | null, message?: string) => ({
  name,
  message: message || 'Custom validation failed',
  validate: validator
})

/**
 * Validator factory that creates validation rules
 * Follows Task 21 specification for individual validator functions
 */
export const validators: ValidatorFactory = {
  required,
  email,
  minLength,
  maxLength,
  pattern,
  numeric,
  phone,
  url,
  custom
}

/**
 * Validation function that validates a single value against multiple rules
 * Returns array of error messages (empty if valid)
 */
export function validateValue(
  value: any, 
  rules: ValidationRule[], 
  context?: ValidationContext
): string[] {
  const errors: string[] = []
  
  for (const rule of rules) {
    const error = rule.validate(value, context?.metadata?.i18n)
    if (error !== null) {
      errors.push(error)
    }
  }
  
  return errors
}

/**
 * Validation function that validates an entire form
 * Returns validation result with all errors
 */
export function validateForm<T>(
  values: T,
  validationRules: Record<keyof T, ValidationRule[]>,
  context?: ValidationContext
): { isValid: boolean; errors: Partial<Record<keyof T, string[]>> } {
  const errors: Partial<Record<keyof T, string[]>> = {}
  let isValid = true

  Object.entries(validationRules).forEach(([field, rules]) => {
    const value = (values as any)[field]
    const fieldContext = { 
      ...context, 
      fieldName: field,
      formData: values as Record<string, any>
    }
    
    const fieldErrors = validateValue(value, rules as ValidationRule[], fieldContext)
    
    if (fieldErrors.length > 0) {
      errors[field as keyof T] = fieldErrors
      isValid = false
    }
  })

  return { isValid, errors }
}